import React, { useState, useEffect } from 'react'
import { always, complement, compose, cond, equals, filter, ifElse, isEmpty, isNil, join, last, prop, props, take, T } from 'ramda'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import add from 'date-fns/add'
import styled from 'styled-components'
import {
  Box,
  Card,
  Flex,
  Link,
  Text
} from 'pcln-design-system'
import { ArrowRight, Icon } from 'pcln-icons'
import { getPersistedSearchDataByKeys } from '../utils/local-forage'
import { capitalizeLetter } from '../utils/string-helpers'
import { handleSubmitForHotels } from './search-forms/utils/hotels-url-formatters'
import {
  HOTEL_PRODUCT_TYPE,
  HOTEL_SEARCH_DATA,
  PACKAGE_SEARCH_DATA,
  RENTAL_CAR_PRODUCT_TYPE,
  STAY,
  VACATION_PACKAGE_PRODUCT_TYPE
} from '../constants/Constants'
import * as PCLNAnalytics from '../utils/PCLN-Analytics'

const DATE_FORMAT = 'E, MMM dd'

const parseLocation = ifElse(
  isNil,
  always(''),
  compose(
    join(', '),
    take(2),
    filter(x => x !== '--'),
    filter(Boolean),
    filter(complement(isEmpty)),
    props([
      'cityName',
      'stateCode',
      'countryCode',
      'itemName'
    ])
  )
)

const filterFalsey = (primary, fallback) => compose(
  last,
  filter(Boolean),
  filter(complement(isEmpty)),
  props(['locationObject', fallback, primary])
)

// we need endLocation & startLocation as a fallback because
// next-landing uses these keys to save package searches
const getStartLocation = filterFalsey('startLocationObject', 'startLocation')
const getEndLocation = filterFalsey('endLocationObject', 'endLocation')

const getProductType = compose(
  capitalizeLetter,
  cond([
    [equals(STAY), always(HOTEL_PRODUCT_TYPE)],
    [equals('AH'), always(RENTAL_CAR_PRODUCT_TYPE)],
    [T, always(VACATION_PACKAGE_PRODUCT_TYPE)]
  ]),
  prop('tripType')
)

function renderIcon (start, end) {
  if (start && end && start !== end) {
    return <ArrowRight fontWeight='bold' size='12' />
  }
}

function formatDates ({ startDate = '', endDate = '' }) {
  if (!startDate) {
    const today = new Date()
    const tomorrow = add(today, { days: 1 })

    return [
      format(today, DATE_FORMAT),
      format(tomorrow, DATE_FORMAT)
    ]
  }

  return [
    format(parse(startDate, 'yyyyMMdd', new Date()), DATE_FORMAT),
    format(parse(endDate, 'yyyyMMdd', new Date()), DATE_FORMAT)
  ]
}

const Wrapper = styled.div`
  background-color: #f4f6f8;
  margin-bottom: -16px;
  padding: 16px 0;
`

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
  margin-left: auto;
  margin-right: auto;
  max-width: 1280px;
  padding-left: 28px;
`

const StyledLink = styled(Link)`
  align-self: flex-end;
  font-size: 12px;
`

const StyledHeading = styled(Text)`
  font-size: 16px;
  font-weight: bold;
  flex-wrap: none;
  margin-bottom: 8px;
  width: 100%;
`

export default function RecentSearchCards () {
  const [recentSearches, setRecentSearches] = useState([])
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    getPersistedSearchDataByKeys([
      HOTEL_SEARCH_DATA,
      PACKAGE_SEARCH_DATA
    ], true).then(data => {
      setRecentSearches(data.slice(0, 2))
    })
  }, [])

  useEffect(() => {
    if (recentSearches.length) {
      setIsLoaded(true)
    }
  }, [recentSearches])

  if (!isLoaded) {
    return null
  }

  const recentSearchCards = recentSearches.map((item, index) => {
    const product = getProductType(item)

    const locationStart = compose(
      parseLocation,
      getStartLocation
    )(item)

    const locationEnd = compose(
      parseLocation,
      getEndLocation
    )(item)

    const [startDate, endDate] = formatDates(item)
    const iconName = product.toLocaleLowerCase() === VACATION_PACKAGE_PRODUCT_TYPE ? 'Luggage' : product
    const arrow = renderIcon(locationStart, locationEnd)

    return (
      <Card
        color='background.lightest'
        boxShadowSize='md'
        mr={index === 0 ? '36px' : 0}
        px='10px'
        py={2}
        height='74px'
        width='388px'
        style={{
          cursor: 'pointer'
        }}
        key={index}
        onClick={() => {
          handleSubmitForHotels({
            values: item,
            onSubmit: url => window.open(url)
          })

          PCLNAnalytics.send({
            action: 'Personalized Shortcut',
            data: 'Recent Search Shortcut'
          })
        }}
      >
        <Flex>
          <Icon color='primary.dark' name={iconName} size='20' mr='10px' />
          <Box color='#001833' style={{ flexGrow: '1' }}>
            <Text fontSize={1} fontWeight='bold' mb={1}>{locationStart} {arrow} {locationStart !== locationEnd && locationEnd}</Text>
            <Text fontSize={0} fontWeight='medium'>{startDate} - {endDate}</Text>
          </Box>
          <StyledLink>Search Again</StyledLink>
        </Flex>
      </Card>
    )
  })

  return (
    <Wrapper>
      <StyledFlex>
        <StyledHeading>Your Recent Searches</StyledHeading>
        {recentSearchCards}
      </StyledFlex>
    </Wrapper>
  )
}
